import React from 'react';
import {
  Position,
  sortByProperty,
  FilterCombo,
  withFormControl,
  PuzzleCheckbox,
  PuzzleRadio,
  PuzzleInput,
  PuzzleColorPicker, PuzzleTagField
} from "puzzle";
import { CheckboxChangeEvent, ColorPickerChangeEvent } from '@progress/kendo-react-inputs';
import { RadioButtonChangeEvent, } from "@progress/kendo-react-inputs";
import * as _ from 'lodash';
import { FILTER, FILTER_DATA, FILTER_FORM_PROPS, FILTER_FORM_STATE } from "./Types";
import { config } from "./FilterForm.config";
import {
  commonLocalizationGetter, getColorPalette,
  getFormattedDate,
  getMappedSubmissionStatuses, PALETTE
} from "../../util/Util";
import CustomFieldsFilter from "./CustomFields/CustomFieldsFilter";
import { Container, FieldWrap } from "puzzle";
import { Field } from "../../UI/Field/Field";
import LocalizedDatePicker from "../../util/LocalizedDatePicker/LocalizedDatePicker";
import RemoteTagField from './RemoteTagField/RemoteTagField';
import FilterFormAbstract from "../../Abstract/Filter/FilterFormAbstract";

export class FilterForm extends FilterFormAbstract<FILTER_FORM_PROPS, FILTER_FORM_STATE> {
  colorPalette: PALETTE = getColorPalette();
  colorPaletteForPicker = _.invert(getColorPalette());
  submissionStatusData = ['ON_HOLD', 'ACTIVE', 'COMPLETED', 'CANCELLED', 'DELIVERED'].map((item: string) => {
    return {
      name: commonLocalizationGetter(`SUBMISSION.statuses.${item}`),
      value: item
    }
  });

  moduleDidMount = () => {
    if (!this.state.filterData) {
      this.dispatch('getSearchBarData', Position.end);
    }
  };

  componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<any>, snapshot?: any): void {
    if (!_.isEqual(prevProps.value, this.props.value)) {
      this.setState({
        values: this.props.value
      });
    }
  };

  /**
   * Checkbox change handler
   */
  onCheckboxChange = (event: CheckboxChangeEvent) => {
    const value = event.value;
    const name = event.nativeEvent.target.name || '';
    this.onFilterChange(name, value);
  };

  /**
   * Decodes and converts data to propper format for edit filter.
   */
  decodeEditData = (data: FILTER) => {
    let decodedFilterData = {
      ...data,
      ...data.searchData ? JSON.parse(data.searchData) : {}
    };
    Object.assign(decodedFilterData,
      decodedFilterData.submissionStatusValues ? { submissionStatusValues: getMappedSubmissionStatuses(decodedFilterData.submissionStatusValues, 'SUBMISSION_STATUSES_MAP_UI') } : null
    );

    delete (decodedFilterData.searchData);
    delete (decodedFilterData.isFavorite);
    delete (decodedFilterData.index);
    delete (decodedFilterData.ascending);
    delete (decodedFilterData.folder);
    this.editFilterInitialData = { ...decodedFilterData, isFavorite: data.isFavorite };
    return decodedFilterData
  };

  /**
   * Returns chosen data from filter form
   * NOTES:
   *    - if there is cancelled status in search criteria then we have to set includeHidden to true regardless its value
   */
  getData = (customAttributesFilter: any, stringifyFormat = true) => {
    let formData = { ...this.state.values };
    delete formData.filterId;
    for (let prop in formData) {
      const isEmpty = _.isEmpty((formData as any)[prop]) && !_.isDate((formData as any)[prop]) && !_.isNumber((formData as any)[prop]) && (!_.isBoolean((formData as any)[prop]) && (formData as any)[prop]);
      if (isEmpty) {
        delete (formData as any)[prop];
      }
    }

    let jsonSearch: any = {
      index: 0,
      ascending: true,
      folder: 'DEFAULT_SUBMISSION'
    };
    const color = formData.color;
    delete formData.color;

    let phasesName = this.getPropertyFromFilterData('PHASES_REMOTE_COMBO', 'name');
    formData = this.parseRemoteComboData(phasesName, formData);

    Object.assign(jsonSearch,
      { ...formData },
      formData.submissionStatusValues?.includes("CANCELLED") ? { includeHidden: true } : null,
      formData.submissionStatusValues ? { submissionStatusValues: getMappedSubmissionStatuses(formData.submissionStatusValues, 'SUBMISSION_STATUSES_MAP_SERVER') } : null,
      customAttributesFilter ? { customControlValues: customAttributesFilter } : null
    );
    delete jsonSearch.filterName;
    const filterName = this.props.form.getData().filterName;
    let filter;
    if (stringifyFormat) {
      filter = {
        color: color,
        filterName,
        isFavorite: this.state.publishOnLandingPageChecked,
        jsonSearch: JSON.stringify(jsonSearch)
      }
    } else {
      filter = jsonSearch;
    }
    return filter;
  };

  /**
   * Checks if object contains some of given props is that is case returns datePickerFormat else returns emptyPickerFormat as DatePickerFormat
   * @param values
   * @param dateReference
   */
  getDateFormat = (values: any, dateReference: string): string => {
    const dateFormat = this.translateByNamespace('date.datePickerFormat')
    return dateFormat
  };

  /**
   * Rearranges quoteStatusData to proper form
   * @param quoteStatusValues
   */
  getMappedQuoteStatusData = (quoteStatusValues: [[string, string]]) => {
    let data: { name: string, value: string }[] = quoteStatusValues.map(item => {
      return {
        name: commonLocalizationGetter(`QUOTES.statuses.${item[1]}`),
        value: item[1]
      }
    });
    data = sortByProperty(data, 'name');

    return data;
  };

  /**
   * Handle radio button change
   */
  handleRadioChange = (event: RadioButtonChangeEvent) => {
    let value = event.value;
    this.setState({
      projectRadio: value
    });

    const { values } = this.state;
    if (value === 'allProjects' && values.projectValues?.length) {
      this.dispatch('getSearchBarData');
    }

    this.onFilterChange('projectValues', null); //clear selected project filter
  };

  /**
   * Populates FilterForm with edit data.
   *
   * @param filterData
   * @param moreData
   */
  loadEditFilterData = (filterData: FILTER_DATA, moreData: FILTER) => {
    const valuesData = this.decodeEditData(moreData);
    const filterName = valuesData.filterName;
    if (filterName) {
      this.props.form.setData({ filterName });
    }
    const subCount = valuesData.submissionCount;
    delete valuesData.submissionCount;
    this.setState({
        publishOnLandingPageChecked: _.isUndefined(moreData.isFavorite) ? true : moreData.isFavorite,
        projectRadio: valuesData.projectValues?.length ? 'selectedProject' : 'allProjects',
        submissionCountFilterResult: subCount,
        filterData: filterData,
        values: _.omit(valuesData, 'customControlValues')
      },
      this.updateDialogFooter
    );
  };

  /**
   * Updates submission counter in FilterForm dialog
   */
  updateDialogFooter = () => {
    const { submissionCountFilterResult } = this.state;
    this.dispatch('updateFilterFormDialogFooter', Position.end, {
      submissionCount: submissionCountFilterResult || 0
    });
  };

  /**
   * Creates flag that indicates was data changed, prepares data for sending.
   */
  saveEditedData = (customControlValues: any) => {
    const filterName = this.props.form.getData().filterName;
    const { filterName: initialFilterName, filterId: initialFilterId } = this.editFilterInitialData;
    let values = { ...this.state.values };
    let phasesName = this.getPropertyFromFilterData('PHASES_REMOTE_COMBO', 'name');
    values = this.parseRemoteComboData(phasesName, values);
    const mergedFilters = {
      ...values,
      filterName,
      customControlValues,
      isFavorite: this.state.publishOnLandingPageChecked
    };
    let dataChange = !_.isEqual(mergedFilters, this.editFilterInitialData);
    return {
      filterName: initialFilterName,
      filterId: initialFilterId,
      filterData: this.getData(customControlValues),
      dataChange
    }
  };

  /**
   * Update filter data
   */
  updateFilterData = (newFilterData: FILTER_DATA) => {
    let { filterData } = this.state;
    // @ts-ignore
    filterData = _.omitBy(filterData, (item) => item.custom);

    filterData = {
      ...filterData,
      ...newFilterData
    };

    this.setState({ filterData });
  };

  /**
   * Creates array of all custom filed filters.
   */
  getAllCustomFieldsFilters = (filterData: any) => {
    return _.transform(filterData, (agr: any[], value: any, key) => {
      if (!!value.custom) {
        agr.push({ ...value })
      }
    }, [])
  };

  renderBody() {
    const { displayFilterName, customAttributesValues, dashboardFilter } = this.props;
    const { getFields, handleChange } = this.props.form;
    const { filterName } = getFields();
    const { filterData, projectRadio, values, publishOnLandingPageChecked } = this.state;
    const customFieldsFilters = this.getAllCustomFieldsFilters(filterData);
    const dashboardClass = dashboardFilter ? 'tp-form-field-dashboard' : '';
    return (filterData &&
      <>
        <div className={'tp-filter-form'}>
          <form className={'k-form'}>
            {displayFilterName &&
              <FieldWrap
                withValidation
                className={`tp-colorpicker-field-wrap ${dashboardClass}`}
              >
                <PuzzleInput
                  asField
                  fieldMandatory
                  label={this.translate('filterName')}
                  id="filterName"
                  name="filterName"
                  onChange={(e: any) => {
                    this.updateFilterValueChanged();
                    handleChange(e);
                  }}
                  errorTxt={this.translateErrorMessage(filterName)}
                  withValidation
                  {...filterName}
                />
                <Field
                  className={this.state.values.color === 'no_color' ? 'tp-transparent-indicator' : ''}>
                  <PuzzleColorPicker
                    ariaLabelledBy={'colorpicker'}
                    value={values.color}
                    dropdownSettings={this.paletteSettings}
                    id={'colorpicker'}
                    label={this.translate('filterColor')}
                    look={'circle'}
                    valueFieldRgb={false}
                    onChange={(event: ColorPickerChangeEvent) => {
                      this.updateFilterValueChanged();
                      //@ts-ignore
                      this.setState((oldState: FILTER_FORM_STATE) => {
                        return {
                          ...oldState,
                          values: {
                            ...oldState.values,
                            color: event.value || undefined
                          }
                        }
                      })
                    }}
                    palette={this.colorPaletteForPicker}
                  />
                </Field>
              </FieldWrap>}
            <div className='tp-checkbox-holder'>
              <PuzzleCheckbox
                className={`${publishOnLandingPageChecked ? 'qa-fakeClass-checked' : ''}`}
                name='publishOnLandingPageCheckbox'
                id="publishOnLandingPageCheckbox"
                label={this.translate('publishOnLandingPage')}
                checked={publishOnLandingPageChecked}
                value={publishOnLandingPageChecked}
                onChange={(event: CheckboxChangeEvent) => {
                  this.updateFilterValueChanged();
                  this.setState({ publishOnLandingPageChecked: event.value });
                }}
              />
            </div>
            <fieldset className={'tp-filter-form-select-project tp-no-border'}>
              <legend className="sr-only">{this.translate('projects')}</legend>
              <FieldWrap layout={[4, 8]} className={'tp-filter-form-select-project-wrap'}>
                <Field
                  className={'tp-filter-form-project-radio-wrap'}
                >
                  <PuzzleRadio
                    checked={projectRadio === 'allProjects'}
                    id={'allProjects'}
                    label={this.translate('allProjects')}
                    name='projects'
                    onChange={this.handleRadioChange}
                    value='allProjects'
                  />
                  <PuzzleRadio
                    checked={projectRadio === 'selectedProject'}
                    id={'selectedProject'}
                    label={this.translate('selectedProject')}
                    name='projects'
                    onChange={this.handleRadioChange}
                    value='selectedProject'
                  />
                </Field>
                <Field>
                  <PuzzleTagField
                    label={this.translate('selectedProject')}
                    id={"projectValues"}
                    name="projectValues"
                    autoClose={false}
                    popupSettings={{ className: this.props.zIndexCls }}
                    data={this.getPrefilteredData('PROJECT_VALUES')}
                    disabled={projectRadio === 'allProjects'}
                    onChange={this.onProjectChange}
                    textField={'name'}
                    value={values.projectValues || []}
                    valueField={'value'}
                  />
                </Field>
              </FieldWrap>
            </fieldset>
            <FieldWrap>
              <PuzzleInput
                asField
                label={this.translate('submissionName')}
                id={"submissionName"}
                name="submissionNameInputText"
                onChange={this.onInputChange}
                prompt={''}
                value={values.submissionNameInputText || ''}
              />
            </FieldWrap>
            <FieldWrap layout={[6, 6]}>
              {filterData.SUBMISSION_STATUS_VALUES &&
                <Field>
                  <PuzzleTagField
                    autoClose={false}
                    data={this.submissionStatusData}
                    label={this.translate('submissionStatus')}
                    id={"submissionStatusValues"}
                    name="submissionStatusValues"
                    onChange={this.onChangeMulti}
                    popupSettings={{ className: this.props.zIndexCls }}
                    textField={'name'}
                    translate={this.translate.bind(this)}
                    value={values.submissionStatusValues || []}
                    valueField={'value'}
                    translateItems={false}
                  />
                </Field>}
              <Field>
                <RemoteTagField
                  label={this.translate('phase')}
                  name={this.getPropertyFromFilterData('PHASES_REMOTE_COMBO', 'name')}
                  id={this.getPropertyFromFilterData('PHASES_REMOTE_COMBO', 'name')}
                  onChange={this.onChangeMulti}
                  textField={'name'}
                  value={(values as any)[this.getPropertyFromFilterData('PHASES_REMOTE_COMBO', 'name')] || []}
                  valueField={'value'}
                  valuesProvider={this.getPropertyFromFilterData('PHASES_REMOTE_COMBO', 'valuesProvider')}
                />
              </Field>
            </FieldWrap>
            <FieldWrap layout={[6, 6]}>
              {filterData.SOURCE_LANGUAGE_VALUES &&
                <Field>
                  <PuzzleTagField
                    label={this.translate('sourceLanguage')}
                    name="sourceLanguageValues"
                    id={"sourceLanguageValues"}
                    autoClose={false}
                    popupSettings={{ className: this.props.zIndexCls }}
                    data={this.getPrefilteredData('SOURCE_LANGUAGE_VALUES')}
                    onChange={this.onChangeMulti}
                    textField={'name'}
                    value={values.sourceLanguageValues || []}
                    valueField={'value'}
                  />
                </Field>}
              {filterData.TARGET_LANGUAGE_VALUES &&
                <Field>
                  <PuzzleTagField
                    label={this.translate('targetLanguage')}
                    id={"targetLanguageValues"}
                    name="targetLanguageValues"
                    autoClose={false}
                    popupSettings={{ className: this.props.zIndexCls }}
                    data={this.getPrefilteredData('TARGET_LANGUAGE_VALUES')}
                    onChange={this.onChangeMulti}
                    textField={'name'}
                    value={values.targetLanguageValues || []}
                    valueField={'value'}
                  />
                </Field>}
            </FieldWrap>
            <FieldWrap layout={[6, 6]}>
              {filterData.QUOTE_STATUSES &&
                <Field>
                  <PuzzleTagField
                    label={this.translate('quoteStatuses')}
                    id={"quoteStatuses"}
                    name="quoteStatuses"
                    autoClose={false}
                    popupSettings={{ className: this.props.zIndexCls }}
                    data={this.getMappedQuoteStatusData(filterData.QUOTE_STATUSES.values)}
                    textField={'name'}
                    onChange={this.onChangeMulti}
                    value={values.quoteStatuses || []}
                    valueField={'value'}
                  />
                </Field>}
              {filterData.PRIORITY_LIST &&
                <Field>
                  <PuzzleTagField
                    label={this.translate('priorityLevel')}
                    id={"priority"}
                    name="priority"
                    autoClose={false}
                    popupSettings={{ className: this.props.zIndexCls }}
                    data={this.getPrefilteredData('PRIORITY_LIST')}
                    textField={'name'}
                    onChange={this.onChangeMulti}
                    value={values.priority || []}
                    valueField={'value'}
                  />
                </Field>
              }
            </FieldWrap>
            <FieldWrap>
              {filterData.SUBMISSION_OWNER_VALUES &&
                <Field>
                  <PuzzleTagField
                    label={this.translate('projectManagers')}
                    id={"submissionOwnerValues"}
                    name="submissionOwnerValues"
                    autoClose={false}
                    popupSettings={{ className: this.props.zIndexCls }}
                    data={this.getPrefilteredData('SUBMISSION_OWNER_VALUES')}
                    textField={'name'}
                    onChange={this.onChangeMulti}
                    value={values.submissionOwnerValues || []}
                    valueField={'value'}
                  />
                </Field>}
            </FieldWrap>
            <FieldWrap
              className={'k-date-field-wrap'}
              layout={[6, 6]}
            >
              <Field className={'!k-d-flex-row k-justify-content-between'}>
                <span className="k-label">{this.translate('dateCreated')}</span>
                <LocalizedDatePicker
                  name="fromDateCreated"
                  id={"fromDateCreated"}
                  onChange={this.onDateChange}
                  popupSettings={{ popupClass: this.props.zIndexCls }}
                  value={(values.fromDateCreated && new Date(values.fromDateCreated)) || null}
                  valid
                  className={'tp-without-error-text tp-100'}
                  max={(values.toDateCreated && new Date(values.toDateCreated)) || undefined}
                  format={this.translateByNamespace('date.datePickerFormat')}
                  formatPlaceholder={getFormattedDate(this.translate)}
                  label={this.translate('from')}
                  ariaLabelLocale={this.translate('SRL-dateCreatedFrom')}
                />
              </Field>
              <Field>
                <LocalizedDatePicker
                  id={"toDateCreated"}
                  name="toDateCreated"
                  value={(values.toDateCreated && new Date(values.toDateCreated)) || null}
                  min={(values.fromDateCreated && new Date(values.fromDateCreated)) || undefined}
                  onChange={this.onDateChange}
                  valid
                  className={'tp-without-error-text'}
                  popupSettings={{ popupClass: this.props.zIndexCls }}
                  format={this.translateByNamespace('date.datePickerFormat')}
                  formatPlaceholder={getFormattedDate(this.translate)}
                  label={this.translate('to')}
                  ariaLabelLocale={this.translate('SRL-dateCreatedTo')}
                />
              </Field>
            </FieldWrap>
            <FieldWrap
              className={'k-date-field-wrap'}
              layout={[6, 6]}
            >
              <Field className={'!k-d-flex-row k-justify-content-between'}>
                <span className="k-label">{this.translate('dueDate')}</span>
                <LocalizedDatePicker
                  name="fromDueDate"
                  id={"fromDueDate"}
                  className={'tp-without-error-text tp-100'}
                  value={(values.fromDueDate && new Date(values.fromDueDate)) || null}
                  max={(values.toDueDate && new Date(values.toDueDate)) || undefined}
                  onChange={this.onDateChange}
                  valid
                  popupSettings={{ popupClass: this.props.zIndexCls }}
                  format={this.translateByNamespace('date.datePickerFormat')}
                  formatPlaceholder={getFormattedDate(this.translate)}
                  label={this.translate('from')}
                  ariaLabelLocale={this.translate('SRL-dueDateFrom')}
                />
              </Field>
              <Field>
                <LocalizedDatePicker
                  name="toDueDate"
                  id={"toDueDate"}
                  className={'tp-without-error-text'}
                  value={(values.toDueDate && new Date(values.toDueDate)) || null}
                  valid
                  min={(values.fromDueDate && new Date(values.fromDueDate)) || undefined}
                  onChange={this.onDateChange}
                  popupSettings={{ popupClass: this.props.zIndexCls }}
                  format={this.translateByNamespace('date.datePickerFormat')}
                  formatPlaceholder={getFormattedDate(this.translate)}
                  label={this.translate('to')}
                  ariaLabelLocale={this.translate('SRL-dueDateTo')}
                />
              </Field>
            </FieldWrap>
            <FieldWrap className={'tp-past-due-field k-date-field-wrap'}>
              <Field className={'tp-past-due-date !k-d-flex-row k-justify-content-between'}>
                <span className="k-label">{this.translate('pastDueLabel')}</span>
                <FilterCombo
                  label={this.translate('pastDue')}
                  id={"pastDueDateComboBox"}
                  name="pastDueDateComboBox"
                  popupSettings={{ className: this.props.zIndexCls }}
                  data={this.getPrefilteredData('PAST_DUE_DATE_LIST')}
                  textField={'name'}
                  onChange={this.onChangeMulti}
                  value={values.pastDueDateComboBox || null}
                  valueAsSimpleType={true}
                  valueField={'value'}
                />
              </Field>
            </FieldWrap>
            <FieldWrap>
              <PuzzleInput
                asField
                id={"fileNameInputText"}
                label={this.translate('fileName')}
                name="fileNameInputText"
                onChange={this.onInputChange}
                prompt={''}
                value={values.fileNameInputText || ''}
              />
            </FieldWrap>
            <CustomFieldsFilter
              parentChange={this.handleParentChange}
              customFieldsFilters={customFieldsFilters}
              customAttributesValues={customAttributesValues}
            />
            <div className={'tp-filter-form-search-options k-hbox k-align-items-center'}>
              <fieldset className={'k-hbox tp-no-border'}>
                <legend className={'sr-only'}>{this.translate('searchOptions')}</legend>
                <Container
                  displayFlex
                  className={'tp-filter-form-search-options-checkbox-wrap'}>
                  <div>
                    <PuzzleCheckbox
                      className={`${values.userSubmissions ? 'qa-fakeClass-checked' : ''}`}
                      name='userSubmissions'
                      label={this.translate('searchMySubmissions')}
                      id="userSubmissions"
                      checked={values.userSubmissions || false}
                      value={values.userSubmissions || false}
                      onChange={this.onCheckboxChange}
                    />
                  </div>
                  <div>
                    <PuzzleCheckbox
                      className={`${values.includeHidden ? 'qa-fakeClass-checked' : ''}`}
                      name='includeHidden'
                      id="includeHidden"
                      label={this.translate('includeHidden')}
                      checked={values.includeHidden || false}
                      value={values.includeHidden || false}
                      onChange={this.onCheckboxChange}
                    />
                  </div>
                </Container>
              </fieldset>
            </div>
          </form>
        </div>
      </>
    )
  }
}

export default withFormControl(config)((FilterForm as any));
